import { Link } from "react-router-dom";
import { Wrench, BookOpen, ArrowUpRight } from "lucide-react";
import {
    getRelatedTools,
    LEARN_LINK_LABELS,
} from "@/lib/tools/tool-config";

/**
 * Sidebar of internal links shown on every /tools/{slug} page.
 *
 * Renders two stacked cards:
 *   1. Related tools (3) — drives sideways navigation between calculators
 *   2. Related guides (1–N) — internal links into /learn for long-form context
 *
 * On lg+ screens, this sits in a sticky right rail. On mobile it stacks
 * below the result. Pure-static content — no API calls.
 */
export default function ToolInternalLinksSidebar({ tool }) {
    if (!tool) return null;
    const related = getRelatedTools(tool, 3);
    const guides = (tool.relatedArticles || [])
        .map((slug) => ({ slug, label: LEARN_LINK_LABELS[slug] || slug }))
        .filter((g) => g.label);

    return (
        <aside
            className="mt-10 lg:mt-0 lg:sticky lg:top-24 space-y-4"
            data-testid="tool-internal-links-sidebar"
            aria-label="Related tools and guides"
        >
            {related.length > 0 && (
                <nav
                    className="bl-card p-5"
                    aria-labelledby="related-tools-heading"
                    data-testid="related-tools-card"
                >
                    <h2
                        id="related-tools-heading"
                        className="font-display font-semibold text-base text-bl flex items-center gap-2"
                    >
                        <Wrench className="h-4 w-4 text-bl-muted" />
                        More buyer tools
                    </h2>
                    <ul className="mt-3 space-y-2.5">
                        {related.map((rt) => (
                            <li key={rt.slug}>
                                <Link
                                    to={`/tools/${rt.slug}`}
                                    className="group block rounded-lg px-2.5 py-2 -mx-2.5 hover:bg-bl-bg transition-colors"
                                    data-testid={`related-tool-link-${rt.slug}`}
                                >
                                    <div className="flex items-start justify-between gap-3">
                                        <span className="text-sm font-medium text-bl leading-snug">
                                            {rt.name}
                                        </span>
                                        <ArrowUpRight className="h-3.5 w-3.5 text-bl-muted group-hover:text-bl shrink-0 mt-0.5" />
                                    </div>
                                    <p className="text-xs text-bl-muted mt-1 leading-relaxed">
                                        {rt.short}
                                    </p>
                                </Link>
                            </li>
                        ))}
                    </ul>
                </nav>
            )}

            {guides.length > 0 && (
                <nav
                    className="bl-card p-5"
                    aria-labelledby="related-guides-heading"
                    data-testid="related-guides-card"
                >
                    <h2
                        id="related-guides-heading"
                        className="font-display font-semibold text-base text-bl flex items-center gap-2"
                    >
                        <BookOpen className="h-4 w-4 text-bl-muted" />
                        Related guides
                    </h2>
                    <ul className="mt-3 space-y-2">
                        {guides.map((g) => (
                            <li key={g.slug}>
                                <Link
                                    to={`/learn/${g.slug}`}
                                    className="group flex items-start justify-between gap-3 text-sm text-bl hover:underline underline-offset-4"
                                    data-testid={`related-guide-link-${g.slug}`}
                                >
                                    <span className="leading-snug">
                                        {g.label}
                                    </span>
                                    <ArrowUpRight className="h-3.5 w-3.5 text-bl-muted shrink-0 mt-0.5" />
                                </Link>
                            </li>
                        ))}
                    </ul>
                </nav>
            )}
        </aside>
    );
}
